import { MAX_PRICE_TIERS, type ProductDetail } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useInvalidateProducts } from '@/entities/product';
import { Button, Input } from '@/shared/ui';
import { useSetPriceTiers } from '../model/use-set-price-tiers';
import { SectionCard } from './section-card';

interface PriceFormValues {
  readonly tiers: { minQty: number; unitPrice: number }[];
}

interface PriceSectionProps {
  readonly productId: string;
  readonly minOrderQty: number;
  readonly priceTiers: ProductDetail['priceTiers'];
  readonly onSaved: (message: string) => void;
}

/** 수량 구간별 단가(원). 첫 구간은 최소 주문 수량에서 시작해야 한다(서버 검증). */
export const PriceSection = ({
  productId,
  minOrderQty,
  priceTiers,
  onSaved,
}: PriceSectionProps): ReactElement => {
  const invalidate = useInvalidateProducts();
  const saveAction = useSetPriceTiers({
    onSuccess: () => {
      onSaved('가격 구간을 저장했습니다.');
      invalidate.detail(productId);
      invalidate.list();
    },
  });

  const { register, control, handleSubmit } = useForm<PriceFormValues>({
    defaultValues: {
      tiers:
        priceTiers.length > 0
          ? priceTiers.map((tier) => ({ minQty: tier.minQty, unitPrice: tier.unitPrice }))
          : [{ minQty: minOrderQty, unitPrice: 0 }],
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'tiers' });

  return (
    <SectionCard
      title="가격"
      description={`첫 구간은 최소 주문 수량(${minOrderQty.toLocaleString('ko-KR')})부터 시작해야 합니다. 단가는 원 단위 정수입니다.`}
      error={saveAction.error?.message ?? null}
    >
      <form
        onSubmit={handleSubmit((values) =>
          saveAction.setPriceTiers({ id: productId, tiers: values.tiers }),
        )}
        className="flex flex-col gap-3"
      >
        {fields.map((field, index) => (
          <div key={field.id} className="flex items-center gap-2">
            <Input
              aria-label={`구간 ${index + 1} 시작 수량`}
              className="w-28"
              type="number"
              min={1}
              {...register(`tiers.${index}.minQty`, { valueAsNumber: true, required: true })}
            />
            <span className="text-sm text-muted-foreground">개 이상 →</span>
            <Input
              aria-label={`구간 ${index + 1} 개당 단가`}
              className="w-32"
              type="number"
              min={1}
              {...register(`tiers.${index}.unitPrice`, { valueAsNumber: true, required: true })}
            />
            <span className="text-sm text-muted-foreground">원</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive"
              aria-label={`구간 ${index + 1} 삭제`}
              disabled={fields.length <= 1}
              onClick={() => remove(index)}
            >
              삭제
            </Button>
          </div>
        ))}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={fields.length >= MAX_PRICE_TIERS}
            onClick={() => append({ minQty: 0, unitPrice: 0 })}
          >
            구간 추가
          </Button>
          <Button type="submit" size="sm" isLoading={saveAction.isPending}>
            가격 저장
          </Button>
        </div>
      </form>
    </SectionCard>
  );
};
